import { useNavigate } from 'react-router-dom';
import { columnCollections, columnArticles } from '@/mocks/column';

interface ColumnCollectionsProps {
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}

export default function ColumnCollections({ selectedId, onSelect }: ColumnCollectionsProps) {
  const navigate = useNavigate();

  return (
    <section className="mb-14">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-black text-gray-900 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-booklet-line text-[#F59E0B]"></i>
          </div>
          主题合集
        </h2>
        <div className="flex items-center gap-3">
          {selectedId && (
            <button
              onClick={() => onSelect(null)}
              className="flex items-center gap-1.5 text-xs text-[#7C3AED] bg-[#7C3AED]/10 px-3 py-1.5 rounded-full hover:bg-[#7C3AED]/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-circle-line"></i>
              清除筛选
            </button>
          )}
          <p className="text-sm text-gray-400">点击合集可过滤展示该主题所有文章</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {columnCollections.map((col, idx) => {
          const articles = col.articleIds.map(id => columnArticles.find(a => a.id === id)).filter(Boolean);
          const totalViews = articles.reduce((s, a) => s + (a?.viewCount ?? 0), 0);
          const isLarge = idx === 0;
          const isSelected = selectedId === col.id;

          return (
            <div
              key={col.id}
              className={`group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 ${isLarge ? 'md:col-span-2' : ''} ${isSelected ? 'ring-2 ring-[#7C3AED] shadow-xl shadow-[#7C3AED]/20 -translate-y-1' : 'hover:-translate-y-1'}`}
              onClick={() => onSelect(isSelected ? null : col.id)}
            >
              {/* Selected indicator ribbon */}
              {isSelected && (
                <div className="absolute top-0 left-0 right-0 z-20 bg-[#7C3AED] text-white text-xs font-bold py-1.5 text-center flex items-center justify-center gap-2">
                  <i className="ri-filter-3-line"></i>
                  正在展示此合集文章 · 点击取消筛选
                </div>
              )}

              {/* Cover Image */}
              <div className={`relative w-full h-52 overflow-hidden ${isSelected ? 'mt-7' : ''}`}>
                <img
                  src={col.coverImage}
                  alt={col.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />

                {/* Selected glow overlay */}
                {isSelected && (
                  <div className="absolute inset-0 bg-[#7C3AED]/15" />
                )}

                {/* Category badge top-left */}
                <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-3 py-1">
                  <i className={`${col.icon} text-white text-xs`}></i>
                  <span className="text-white text-xs font-semibold">{col.articleIds.length} 篇</span>
                </div>

                {/* Calligraphy author name */}
                <div className="absolute top-3 right-3 flex flex-col items-end" style={{ maxWidth: '30%' }}>
                  <div
                    className="text-white/80 text-right leading-tight tracking-widest"
                    style={{
                      fontFamily: '"STKaiti", "KaiTi", "华文楷体", "楷体", serif',
                      fontSize: '13px',
                      textShadow: '0 1px 4px rgba(0,0,0,0.8)',
                      writingMode: 'vertical-rl',
                      maxHeight: '70px',
                    }}
                  >
                    清鉴元衡
                  </div>
                </div>

                {/* Bottom content */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: col.accentColor + '30', border: `1px solid ${col.accentColor}60` }}
                    >
                      <i className={`${col.icon} text-xs`} style={{ color: col.accentColor }}></i>
                    </div>
                    <h3 className="text-white font-black text-base leading-tight">{col.title}</h3>
                  </div>
                  <p className="text-white/60 text-xs leading-relaxed line-clamp-2">{col.description}</p>
                </div>
              </div>

              {/* Footer */}
              <div
                className="px-4 py-3 flex items-center justify-between"
                style={{ background: isSelected ? `linear-gradient(135deg, #7C3AED, #6D28D9)` : `linear-gradient(135deg, ${col.themeColor}ee, ${col.themeColor}cc)` }}
              >
                <div className="flex items-center gap-3 text-white/60 text-xs">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {(totalViews / 1000).toFixed(1)}k 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-article-line"></i>
                    {articles.length} 篇文章
                  </span>
                </div>
                <div className={`flex items-center gap-1 text-xs font-semibold transition-all ${isSelected ? 'text-white gap-2' : 'text-white/80 group-hover:gap-2'}`}>
                  {isSelected ? (
                    <><i className="ri-check-line"></i>已选中</>
                  ) : (
                    <>展开合集 <i className="ri-arrow-right-line"></i></>
                  )}
                </div>
              </div>

              {/* Article preview list */}
              <div className={`border-t-0 border border-gray-100 px-4 py-3 transition-colors ${isSelected ? 'bg-[#7C3AED]/5' : 'bg-white'}`}>
                <div className="space-y-1.5">
                  {articles.slice(0, 2).map(a => a && (
                    <div
                      key={a.id}
                      onClick={e => { e.stopPropagation(); navigate(`/column/${a.id}`); }}
                      className="flex items-start gap-2 cursor-pointer group/item"
                    >
                      <i className={`ri-file-text-line text-xs mt-0.5 shrink-0 ${isSelected ? 'text-[#7C3AED]/50' : 'text-gray-300'}`}></i>
                      <p className={`text-xs group-hover/item:text-[#7C3AED] transition-colors line-clamp-1 leading-relaxed ${isSelected ? 'text-[#7C3AED]/70' : 'text-gray-500'}`}>
                        {a.title}
                      </p>
                    </div>
                  ))}
                  {articles.length > 2 && (
                    <p className={`text-xs pl-4 ${isSelected ? 'text-[#7C3AED]/50' : 'text-gray-400'}`}>…共 {articles.length} 篇</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
